import { Star } from "lucide-react";

const WRESTLING_IMG = "/wrestling-match.jpg";

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="aged-paper">
      {/* Header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">The Story</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="about-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            About the Club
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
          <p className="font-body text-sm text-muted-foreground uppercase tracking-widest mt-4">
            Est. 2026 &mdash; The Piedmont Territory
          </p>
        </div>
      </section>

      <div className="vintage-divider" />

      {/* Main content */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Image */}
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={WRESTLING_IMG}
                alt="Professional Wrestling Match"
                data-testid="about-image"
                className="w-full h-80 sm:h-[520px] object-cover vintage-img"
              />
              <div className="h-2 bg-primary" />
              <div className="absolute top-4 left-4 bg-[#1A1510]/80 px-3 py-2">
                <p className="font-heading text-xs text-[#B09030] uppercase tracking-wider">The Ring</p>
              </div>
            </div>
            {/* Pullquote */}
            <div className="bg-secondary p-6 sm:p-8 mt-0">
              <Star className="w-4 h-4 fill-[#B09030] text-[#B09030] mb-3" />
              <p className="font-heading text-lg sm:text-xl text-[#E8DFC8] uppercase leading-snug">
                "Every show is a return to form. Every match matters."
              </p>
              <div className="h-[1px] w-12 bg-[#B09030] mt-4" />
            </div>
          </div>

          {/* Text */}
          <div className="md:col-span-7">
            <h2 className="font-heading text-3xl sm:text-4xl uppercase mb-2 leading-tight">
              The Territory Lives
            </h2>
            <div className="h-[3px] w-16 gold-accent mb-8" />
            <div className="font-body text-base leading-relaxed space-y-6 text-foreground/85">
              <p className="first-letter:text-4xl first-letter:font-heading first-letter:float-left first-letter:mr-2 first-letter:mt-1 first-letter:leading-none">
                The Piedmont Wrestling Club was founded on one principle: professional wrestling
                was at its best when the territories thrived. When local promotions built
                local heroes, and every card meant something to the people in those seats.
              </p>
              <p>
                We are bringing that spirit back. No scripts read off teleprompters. No
                million-dollar pyrotechnics hiding mediocre matches. No corporate booking
                by committee. Just honest, hard-hitting professional wrestling the way
                it was meant to be presented — in front of fans who <em>care.</em>
              </p>
              <p>
                Based in the Piedmont region, the PWC draws on the rich history of
                Southern wrestling — the Mid-Atlantic territory, the Crockett era,
                and the smoky armories where legends were born and careers were forged
                under hot lights and hotter crowds.
              </p>
              <p>
                This is wrestling for the people who remember what it felt like when
                the territories ran. And for a new generation that deserves to
                experience it for the first time.
              </p>
            </div>

            {/* Philosophy blocks */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-0">
              {[
                { title: "Tradition", desc: "Honoring the legacy and lineage of territory-era professional wrestling." },
                { title: "Competition", desc: "Every bout fought with purpose, pride, and something on the line." },
                { title: "Community", desc: "Wrestling belongs to the fans. The territory belongs to the people." },
              ].map((item, idx) => (
                <div
                  key={item.title}
                  data-testid={`about-pillar-${item.title.toLowerCase()}`}
                  className={`p-6 sm:p-8 ${idx === 1 ? 'bg-secondary' : 'bg-foreground'}`}
                >
                  <Star className="w-3 h-3 fill-[#B09030] text-[#B09030] mb-3" />
                  <h3 className="font-heading text-lg uppercase mb-2 text-[#E8DFC8]">{item.title}</h3>
                  <p className="font-body text-sm text-[#E8DFC8]/60 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
